import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Eye, MousePointerClick, Power } from "lucide-react";
import { api } from "@/lib/api";
import { AudienceChart } from "@/pages/Advertise";

const inp = "w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-violet-300";
const err = (e) => {
  const d = e.response?.data?.detail;
  toast.error(Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d || "Action failed");
};
const ctr = (s) => (s?.impressions ? `${((s.clicks / s.impressions) * 100).toFixed(1)}%` : "—");
const L = ({ label, children }) => <label className="block"><span className="block text-[11px] font-medium text-slate-500 mb-1">{label}</span>{children}</label>;

const Toggle = ({ on, onClick, testid, label }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${on ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>
    <Power size={12} /> {label}: {on ? "ON" : "OFF"}
  </button>
);

const StatPair = ({ s }) => (
  <span className="inline-flex items-center gap-3 text-xs text-slate-600 font-mono">
    <span className="inline-flex items-center gap-1"><Eye size={12} /> {s?.impressions || 0}</span>
    <span className="inline-flex items-center gap-1"><MousePointerClick size={12} /> {s?.clicks || 0}</span>
    <span>CTR {ctr(s)}</span>
  </span>
);

export function AdsPanel() {
  const qc = useQueryClient();
  const { data: st } = useQuery({ queryKey: ["admin-ad-settings"], queryFn: async () => (await api.get("/admin/ads/settings")).data });
  const { data: stats } = useQuery({ queryKey: ["admin-ad-stats"], queryFn: async () => (await api.get("/admin/ads/stats")).data });
  const [f, setF] = useState(null);
  useEffect(() => { if (st) setF(st); }, [st]);

  const save = async (next = f) => {
    try { await api.put("/admin/ads/settings", next); toast.success("Ad settings saved"); qc.invalidateQueries(); } catch (x) { err(x); }
  };
  if (!f) return null;
  const slot = f.ad_slot;
  const setSlot = (k, v) => setF({ ...f, ad_slot: { ...slot, [k]: v } });

  return (
    <div className="space-y-6">
      <h1 className="font-display font-bold text-2xl tracking-tight">Advertising</h1>

      <section data-testid="ad-slot-settings" className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div><h2 className="font-semibold text-slate-800">Homepage banner (middle of page)</h2><StatPair s={stats?.ad_slot} /></div>
          <Toggle testid="toggle-ad-slot" on={slot.enabled} label="Banner" onClick={() => { const n = { ...f, ad_slot: { ...slot, enabled: !slot.enabled } }; setF(n); save(n); }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <L label="Banner image URL (https, recommended 1456×180 or 970×250)"><input data-testid="ad-image-url" value={slot.image_url} onChange={(e) => setSlot("image_url", e.target.value)} className={inp} placeholder="https://..." /></L>
          <L label="Destination URL"><input data-testid="ad-target-url" value={slot.target_url} onChange={(e) => setSlot("target_url", e.target.value)} className={inp} placeholder="https://advertiser.com" /></L>
          <L label="Advertiser name"><input data-testid="ad-advertiser" value={slot.advertiser} onChange={(e) => setSlot("advertiser", e.target.value)} className={inp} /></L>
          <L label="Alt text"><input data-testid="ad-alt" value={slot.alt_text} onChange={(e) => setSlot("alt_text", e.target.value)} className={inp} /></L>
        </div>
        <p className="text-[11px] text-muted-foreground">Leave image or destination empty to show the default “Advertise Here / أعلن هنا” placeholder.</p>
        {slot.image_url && <img src={slot.image_url} alt="preview" className="w-full max-h-40 object-cover rounded-lg border border-slate-200" />}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div><h2 className="font-semibold text-slate-800">Sponsored cards in the feed</h2><span className="text-xs text-muted-foreground">Placeholder (no active cards): </span><StatPair s={stats?.sponsored_placeholder} /></div>
          <Toggle testid="toggle-sponsored" on={f.sponsored_enabled} label="Sponsored" onClick={() => { const n = { ...f, sponsored_enabled: !f.sponsored_enabled }; setF(n); save(n); }} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <L label="Show a sponsored card after every X stories"><input data-testid="sponsored-every" type="number" min={2} max={30} value={f.sponsored_every} onChange={(e) => setF({ ...f, sponsored_every: Number(e.target.value) })} className={inp} /></L>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700"><input data-testid="stats-public" type="checkbox" checked={f.stats_public} onChange={(e) => setF({ ...f, stats_public: e.target.checked })} /> Show audience statistics publicly on /advertise</label>
        </div>
      </section>

      <button data-testid="save-ad-settings" onClick={() => save()} className="bg-violet-600 text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-violet-700">Save ad settings</button>

      <SponsoredManager />
    </div>
  );
}

const EMPTY = { enabled: true, sponsor: "", title_en: "", title_ar: "", desc_en: "", desc_ar: "", image_url: "", target_url: "" };

function SponsoredManager() {
  const qc = useQueryClient();
  const { data: cards } = useQuery({ queryKey: ["admin-sponsored"], queryFn: async () => (await api.get("/admin/sponsored")).data });
  const [edit, setEdit] = useState(null);
  const refresh = () => qc.invalidateQueries();

  const submit = async (e) => {
    e.preventDefault();
    const { id, impressions, clicks, created_at, ...body } = edit;
    try {
      if (id) await api.put(`/admin/sponsored/${id}`, body); else await api.post("/admin/sponsored", body);
      toast.success("Sponsored card saved"); setEdit(null); refresh();
    } catch (x) { err(x); }
  };
  const toggle = async (c) => {
    const { id, impressions, clicks, created_at, ...body } = c;
    try { await api.put(`/admin/sponsored/${id}`, { ...body, enabled: !c.enabled }); refresh(); } catch (x) { err(x); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete sponsored card "${c.title_en}"?`)) return;
    try { await api.delete(`/admin/sponsored/${c.id}`); toast.success("Deleted"); refresh(); } catch (x) { err(x); }
  };
  const field = (k) => ({ value: edit[k], onChange: (e) => setEdit({ ...edit, [k]: e.target.value }), className: inp });

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-semibold text-slate-800">Sponsored cards ({cards?.length || 0}) — active cards rotate in the feed</h2>
        <button data-testid="add-sponsored" onClick={() => setEdit({ ...EMPTY })} className="inline-flex items-center gap-1 text-sm font-semibold text-violet-700"><Plus size={15} /> Add card</button>
      </div>
      {edit && (
        <form data-testid="sponsored-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5 p-4 rounded-xl bg-violet-50/40 border border-violet-100">
          <L label="Sponsor name *"><input data-testid="sp-sponsor" required {...field("sponsor")} /></L>
          <L label="Destination URL * (https)"><input data-testid="sp-target" required {...field("target_url")} /></L>
          <L label="Title (English) *"><input data-testid="sp-title-en" required {...field("title_en")} /></L>
          <L label="Title (Arabic)"><input data-testid="sp-title-ar" dir="rtl" {...field("title_ar")} /></L>
          <L label="Description (English)"><textarea data-testid="sp-desc-en" rows={2} {...field("desc_en")} /></L>
          <L label="Description (Arabic)"><textarea data-testid="sp-desc-ar" rows={2} dir="rtl" {...field("desc_ar")} /></L>
          <L label="Image URL (https, 16:9)"><input data-testid="sp-image" {...field("image_url")} /></L>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 self-end"><input type="checkbox" checked={edit.enabled} onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })} /> Active</label>
          <div className="md:col-span-2 flex gap-3">
            <button data-testid="sp-save" className="bg-violet-600 text-white text-sm font-semibold px-5 py-2 rounded-full">Save card</button>
            <button type="button" onClick={() => setEdit(null)} className="text-sm text-slate-500">Cancel</button>
          </div>
        </form>
      )}
      <div className="divide-y divide-slate-100">
        {cards?.map((c) => (
          <div key={c.id} data-testid="sponsored-row" className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
            <div className="min-w-0 flex items-center gap-3">
              {c.image_url ? <img src={c.image_url} alt="" className="w-16 h-9 object-cover rounded" /> : <span className="w-16 h-9 rounded bg-amber-100" />}
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{c.title_en} <span className="text-xs text-muted-foreground">· {c.sponsor}</span></div>
                <StatPair s={c} />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Toggle testid="sp-toggle" on={c.enabled} label="Active" onClick={() => toggle(c)} />
              <button data-testid="sp-edit" onClick={() => setEdit({ ...c })} className="p-1.5 text-slate-500 hover:text-violet-700"><Pencil size={15} /></button>
              <button data-testid="sp-delete" onClick={() => remove(c)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!cards?.length && <div className="py-6 text-center text-sm text-muted-foreground">No sponsored cards yet — the feed shows the “Advertise with us” placeholder.</div>}
      </div>
    </section>
  );
}

export function TrafficPanel() {
  const { data } = useQuery({ queryKey: ["admin-ad-stats"], queryFn: async () => (await api.get("/admin/ads/stats")).data });
  const tr = data?.traffic;
  const cards = [
    ["Unique visitors (30d)", tr?.unique_visitors_30d], ["Page views (30d)", tr?.page_views_30d], ["Visitors today", tr?.visitors_today],
    ["Page views today", tr?.page_views_today], ["Avg daily visitors", tr?.avg_daily_visitors], ["Pages per visit", tr?.pages_per_visit],
  ];
  return (
    <div>
      <h1 className="font-display font-bold text-2xl tracking-tight mb-1">Traffic</h1>
      <p className="text-sm text-muted-foreground mb-4">Anonymous first-party measurement (admin pages excluded). Public version: /advertise.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map(([l, v]) => <div key={l} data-testid="traffic-card" className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="font-display font-bold text-2xl mt-1">{v ?? "—"}</div></div>)}
      </div>
      <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">{tr && <AudienceChart daily={tr.daily} />}</div>
    </div>
  );
}
